// @givmo/board — who presided at a meeting (CBG-YR3-ROLES-PRESIDING) and the minutes a meeting's
// motion may approve (CBG-FIX57), read tolerantly (backend `board_minutes.md` §P3, §P9, §P11).
//
// ══ THE SERVER DECIDES; THIS MODULE ONLY READS ════════════════════════════════════════════════
//
// Who presided is the attendance row's own `presided`, recorded by the officer — never derived from a
// title. Whom the board's duties SUGGEST, and what the minutes will print for each person present, come
// from the presiding read (`GET …/presiding`): the backend reads the titles each person held ON THE
// MEETING'S DAY, the minutes' own capacity rule (the orchestrator's C3), which this console can't
// compute. And which minutes a motion here may approve is the server's list too, decided by the same
// predicates the motion's own start checks (`create_motion`), so this console never re-derives a
// board's approval rule (RC11's C2).
//
// Tolerant like every Board decode: a malformed row is dropped, an unexpected shape reads as nothing,
// and nothing here throws.

import type { BoardMeetingAttendanceOut } from '@givmo/api-client';
import { extractList } from '@givmo/api-client/list';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

/** The person recorded as presiding at the meeting — the one attendance row that says so — or null. */
export function presidingSeatId(rows: readonly BoardMeetingAttendanceOut[]): string | null {
  const row = rows.find((candidate) => candidate.presided === true);
  return row?.board_member_id ?? null;
}

/** Whom the board's duties suggest presided, and why. */
export interface PresidingSuggestion {
  seatId: string;
  /** `presides`, or `presides_in_absence` when nobody present held the title that presides. */
  actKey: 'presides' | 'presides_in_absence';
  /** The title the duty names, in the board's words. */
  titleLabel: string;
}

/** A person's capacity on the MEETING's day: the facts the minutes print beside the name. */
export interface DayCapacity {
  isDirector: boolean;
  titleLabels: readonly string[];
}

/** The presiding read, as the dialog uses it. */
export interface PresidingRead {
  presidingId: string | null;
  /** Each person recorded as present → what the minutes print for them presiding. */
  minutesNames: ReadonlyMap<string, string>;
  /**
   * Each person recorded as present → their capacity on the meeting's day (review-138's F5): the
   * dialog labels them by it, as the suggestion and the minutes read that day. Absent where the read
   * can't say (`is_director` null, or an unreadable row): the dialog then shows the name alone.
   */
  capacities: ReadonlyMap<string, DayCapacity>;
  suggestion: PresidingSuggestion | null;
}

const NO_READ: PresidingRead = {
  presidingId: null,
  minutesNames: new Map(),
  capacities: new Map(),
  suggestion: null,
};

/** `GET …/presiding`'s answer, tolerantly: any part that doesn't read is absent. Never throws. */
export function decodePresiding(body: unknown): PresidingRead {
  if (!isRecord(body)) return NO_READ;
  const minutesNames = new Map<string, string>();
  const capacities = new Map<string, DayCapacity>();
  for (const row of Array.isArray(body.candidates) ? body.candidates : []) {
    if (!isRecord(row)) continue;
    const seat = text(row.board_member_id);
    if (seat === null) continue;
    const name = text(row.minutes_name);
    if (name !== null) minutesNames.set(seat, name);
    // The day's capacity is read only when both facts are: a director fact the roster settled, and
    // a list of title words. Anything else leaves the person to be named alone.
    const labels = Array.isArray(row.title_labels) ? row.title_labels : null;
    if (
      typeof row.is_director === 'boolean' &&
      labels !== null &&
      labels.every((label) => typeof label === 'string' && label.trim().length > 0)
    ) {
      capacities.set(seat, {
        isDirector: row.is_director,
        titleLabels: (labels as string[]).map((label) => label.trim()),
      });
    }
  }
  let suggestion: PresidingSuggestion | null = null;
  if (isRecord(body.suggestion)) {
    const seatId = text(body.suggestion.board_member_id);
    const actKey = body.suggestion.act_key;
    const titleLabel = text(body.suggestion.title_label);
    if (
      seatId !== null &&
      titleLabel !== null &&
      (actKey === 'presides' || actKey === 'presides_in_absence')
    ) {
      suggestion = { seatId, actKey, titleLabel: titleLabel.trim() };
    }
  }
  return {
    presidingId: text(body.presiding_board_member_id),
    minutesNames,
    capacities,
    suggestion,
  };
}

/** One set of minutes a motion at this meeting may approve, named by public facts only. */
export interface ApprovableMinutes {
  recordId: string;
  meetingId: string;
  meetingTitle: string | null;
  meetingStartsAt: string | null;
  meetingTimeZone: string | null;
  /** A closed session's own minutes: its general purpose, never its title. */
  closedSession: { segmentId: string; purposeLabel: string | null } | null;
}

function approvable(row: unknown): ApprovableMinutes | null {
  if (!isRecord(row)) return null;
  const recordId = text(row.record_id);
  const meetingId = text(row.meeting_id);
  if (recordId === null || meetingId === null) return null;
  let closedSession: ApprovableMinutes['closedSession'] = null;
  if (isRecord(row.closed_session)) {
    const segmentId = text(row.closed_session.segment_id);
    if (segmentId === null) return null;
    closedSession = { segmentId, purposeLabel: text(row.closed_session.purpose_label) };
  }
  return {
    recordId,
    meetingId,
    meetingTitle: text(row.meeting_title),
    meetingStartsAt: text(row.meeting_starts_at),
    meetingTimeZone: text(row.meeting_time_zone),
    closedSession,
  };
}

/** `GET …/approvable-minutes`'s list, in the server's order; a malformed row is dropped. */
export function decodeApprovableMinutes(body: unknown): ApprovableMinutes[] {
  return extractList<unknown>(body, 'data')
    .map(approvable)
    .filter((row): row is ApprovableMinutes => row !== null);
}
